import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Marquee from "@/components/Marquee";
import { Reveal, Eyebrow } from "@/components/Reveal";
import { IMG, RETREATS } from "@/data/content";

export default function Retreats() {
  return (
    <main data-testid="retreats-page">
      <section className="relative flex min-h-[70vh] items-end overflow-hidden bg-[#1E1610]">
        <img src={IMG.retreat1} alt="A retreat gathering on the Anglesey coast" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#1E1610] via-[#1E1610]/50 to-[#1E1610]/20" />
        <div className="absolute inset-x-0 top-0 h-32 bg-gradient-to-b from-[#1E1610]/70 to-transparent" />
        <div className="relative z-10 mx-auto w-full max-w-7xl px-4 pb-14 pt-40 sm:px-8 lg:px-16">
          <Reveal>
            <Eyebrow light>Retreats at the Plas</Eyebrow>
            <h1 className="mt-4 max-w-3xl font-serif text-4xl font-light leading-[1.05] tracking-tight text-[#F4F1EA] sm:text-5xl lg:text-6xl">
              Premium escapes, rooted in wild beauty.
            </h1>
            <p className="mt-5 max-w-xl text-[#F4F1EA]/75">
              Designed for groups and solo guests alike — choose an experience, choose your dates, and enquire. We'll
              handle the rest.
            </p>
          </Reveal>
        </div>
      </section>

      <Marquee />

      <section data-testid="retreats-list" className="mx-auto max-w-7xl px-4 py-20 sm:px-8 lg:px-16 lg:py-28">
        <div className="space-y-16">
          {RETREATS.map((r, i) => (
            <Reveal key={r.title}>
              <div className={`grid items-center gap-10 lg:grid-cols-2 lg:gap-16`}>
                <div className={i % 2 === 1 ? "lg:order-2" : ""}>
                  <div className="overflow-hidden rounded-3xl">
                    <img src={r.img} alt={r.title} className="aspect-[4/3] w-full object-cover transition-transform duration-700 hover:scale-105" />
                  </div>
                </div>
                <div className={i % 2 === 1 ? "lg:order-1" : ""}>
                  <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#B85D43]">0{i + 1}</p>
                  <h2 className="mt-3 font-serif text-3xl tracking-tight text-[#241A12] sm:text-4xl">{r.title}</h2>
                  <p className="mt-5 max-w-lg leading-relaxed text-[#7A6A58]">{r.copy}</p>
                  <Link
                    to="/enquire"
                    data-testid={`retreat-enquire-${i}`}
                    className="group mt-7 inline-flex items-center gap-3 font-mono text-xs uppercase tracking-[0.2em] text-[#B85D43] transition-colors hover:text-[#5A4632]"
                  >
                    Enquire about this retreat
                    <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                  </Link>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section data-testid="retreats-host" className="bg-[#5A4632] py-20 text-[#F4F1EA] lg:py-28">
        <div className="mx-auto grid max-w-7xl items-center gap-12 px-4 sm:px-8 lg:grid-cols-2 lg:gap-20 lg:px-16">
          <Reveal>
            <Eyebrow light>Host your own</Eyebrow>
            <h2 className="mt-4 font-serif text-3xl leading-[1.15] tracking-tight sm:text-4xl">
              Bring your people. We'll bring the place.
            </h2>
            <p className="mt-6 leading-relaxed text-[#F4F1EA]/70">
              Yoga teachers, founders, creatives and crews of old friends — the estate hosts private retreats of every
              shape. A wild pond for cold dips, hot tubs for after, fire pits for the evenings and the farmhouse table
              for everything in between.
            </p>
            <Link
              to="/enquire"
              data-testid="retreats-host-enquire"
              className="group mt-9 inline-flex items-center gap-3 rounded-full bg-[#B85D43] px-8 py-4 font-mono text-xs uppercase tracking-[0.25em] text-[#F4F1EA] transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#C89D56] hover:text-[#1E1610]"
            >
              Plan your retreat
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="grid grid-cols-2 gap-4">
              <img src={IMG.retreat3} alt="Wellness on the estate" className="mt-8 aspect-[3/4] w-full rounded-2xl object-cover" />
              <img src={IMG.retreat4} alt="Movement retreat outdoors" className="aspect-[3/4] w-full rounded-2xl object-cover" />
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
